import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type Socket } from 'socket.io-client';

import type { AugmentedError } from '@/types';

import SocketContext from '@/contexts/socket/socket-context';
import {
  GlobalCache,
  GlobalSubscriptionManager,
} from '@/contexts/socket/socket-subscription-manager';

interface UseSocketSubscriptionOptions<T> {
  params?: unknown;
  enabled?: boolean;
  responseEvent?: string;
  keepPreviousData?: boolean;
  filter?: (data: T) => boolean;
}
// TODO: use toSnakeCase to accept camelCase params in https://welldev-group.atlassian.net/browse/RHB-669
const useSocketSubscription = <T>(
  apiName: string,
  {
    params = {},
    enabled = true,
    responseEvent,
    keepPreviousData = true,
    filter,
  }: UseSocketSubscriptionOptions<T> = {}
) => {
  const socket: Socket | null = useContext(SocketContext);

  const paramsString = JSON.stringify(params);
  const memoizedParams = useMemo(() => JSON.parse(paramsString), [paramsString]);
  const cacheKey = `${apiName}:${paramsString}`;

  const [data, setData] = useState<T | null>(GlobalCache.get<T>(cacheKey));
  const [isLoading, setIsLoading] = useState(!data && enabled);
  const [error, setError] = useState<Error | null>(null);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    if (!socket || !enabled) {
      setIsLoading(false);

      return;
    }

    setError(null);

    const cached = GlobalCache.get<T>(cacheKey);

    if (cached && keepPreviousData) {
      setData(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);

      if (!keepPreviousData) {
        setData(null);
      }
    }

    const callback = (responseData: T | null, responseError: Error | null) => {
      if (responseError) {
        setError(responseError as AugmentedError);
        setIsLoading(false);
      } else {
        if (filterRef.current && responseData !== null && !filterRef.current(responseData)) {
          return;
        }

        setError(null);
        setData(responseData);
        GlobalCache.set(cacheKey, responseData);
        setIsLoading(false);
      }
    };

    GlobalSubscriptionManager.subscribe(socket, apiName, memoizedParams, callback);

    return () => {
      GlobalSubscriptionManager.unsubscribe(socket, apiName, memoizedParams, callback);
    };
  }, [
    socket,
    apiName,
    paramsString,
    enabled,
    responseEvent,
    refetchIndex,
    keepPreviousData,
    memoizedParams,
  ]);

  const refetch = useCallback(() => {
    setRefetchIndex((prev) => prev + 1);
  }, []);

  return { data, isLoading, error, refetch };
};

export default useSocketSubscription;
